/**
 * The identity and access vocabulary, shared by the API and the web app.
 *
 * Keys are SCREAMING_SNAKE per CLAUDE.md §3, and every set is a `readonly` tuple so it
 * can feed a `z.enum` on the server and a `parseAsStringLiteral` on the client from ONE
 * declaration. Two copies of an allowlist is one copy too many.
 */

// ── Org tree ─────────────────────────────────────────────────────────────────────

/**
 * PA_REQUIREMENTS §4.2. The tree is COMMISSIONERATE → DIVISION → UNIT today and may be
 * deeper tomorrow: `type` is a LABEL on a node, not a statement about its depth.
 * Nothing in this codebase derives depth from type or vice versa.
 */
export const ORG_UNIT_TYPES = ['COMMISSIONERATE', 'DIVISION', 'UNIT'] as const;
export type OrgUnitType = (typeof ORG_UNIT_TYPES)[number];

/**
 * So non-PS units fit without a schema change (PA_REQUIREMENTS §4.2).
 *
 * ─── [amended INGEST] `WOMEN_PS` IS THE FOURTH KIND OF CHILD A DIVISION HAS ──────
 *
 * The establishment now names exactly four things beneath a division — police station,
 * WOMEN'S police station, DRO, Others — and the first two were one value until this
 * pass. Widening the tuple is the whole of the change: `RESERVE_FORCE` (DRO) and
 * `OTHER` (Others) have been here since P1a, and `divisionUnits.seed.ts`'s header
 * records that neither needed an enum.
 *
 * ⚠️ IT IS NOT A `PS` WITH A DIFFERENT NAME, AND THE DECIDING REASON IS THE INGEST'S
 * REPORT. `officerIngest` must tell an operator which of the four a section resolved to
 * — that is §5.2's whole job — and "look for `(W)` in the name" is a rule that lives in
 * a string, is invisible to the type system, and is wrong the first time a station is
 * renamed. A sub-type is a column the report reads and the compiler checks.
 *
 * ⚠️ ADDING A MEMBER BREAKS THE WEB BUILD UNTIL IT IS LABELLED, WHICH IS THE POINT.
 * `web/src/lib/labels.ts`'s `ORG_UNIT_SUBTYPE_LABEL` is `Record<OrgUnitSubType, string>`
 * — exhaustive, so a new member is a compile error rather than a blank badge. That is
 * the same guard `i18n.test.ts` gives the officer app for its own enums.
 *
 * ⚠️ NOTHING DERIVES `type` FROM `subType`. A women's police station is `type: 'UNIT'`
 * + `subType: 'WOMEN_PS'`, exactly as an ordinary one is `UNIT` + `PS`.
 *
 * ─── [amended WINGS] `WING` IS A DIVISION THAT IS NOT TERRITORIAL ───────────────
 *
 * The commissionerate has ten TERRITORIAL divisions — Central, North, Port — each with
 * police stations beneath it, and twenty-six FUNCTIONAL ones the client posts officers
 * to in exactly the same column: Detective Department, Special Branch, Traffic Police,
 * eight armed battalions, a training school. Both are `type: 'DIVISION'`, because the
 * officer export's `UNIT` column names them interchangeably and `officerIngest`'s
 * resolver matches on that type alone.
 *
 * ⚠️ IT EXISTS TO ANSWER ONE QUESTION, AND THE QUESTION IS "DOES THIS GET A DRO?".
 * A DRO is the reserve office standing behind a division's STATIONS, so a division with
 * none has no use for one. Before this member the two classes were indistinguishable —
 * all ten territorial divisions carry `sub_type: 'OTHER'`, the placeholder — so
 * `seed:division-units` would have created twenty-six unwanted reserve offices the next
 * time anybody ran it, which is a landmine rather than a decision.
 * `DIVISION_UNIT_KINDS` in `divisionUnits.seed.ts` now names the sub-types each kind
 * applies to, and `WING` is the whole of what it excludes.
 *
 * ⚠️ THE ELEVEN BATTALIONS ARE `WING`, NOT `BATTALION`, AND THAT IS DELIBERATE.
 * `BATTALION` was minted for a UNIT beneath a division — a battalion as an operational
 * formation. These are commissionerate-level directorates that happen to be battalions,
 * and giving them the operational sub-type would put them in the same class as a node
 * two levels down while ALSO reopening the DRO question one row at a time. The
 * battalion-ness is in the name, which is where the client put it.
 *
 * ⚠️ IT IS NOT A NEW `type`. `ORG_UNIT_TYPES` is the SHAPE of the tree and three
 * members of it are load-bearing in fourteen files; this is a CLASSIFICATION of one
 * shape, which is exactly what a sub-type is for.
 *
 * ─── [amended OTHERS-EXPAND] `SECTION` IS THE FIFTH KIND, AND IT NARROWS `Others` ─────
 *
 * "Exactly four things beneath a division" above is no longer the whole of it. The
 * `Others` holding unit files every officer whose section matched no station, alias or DRO
 * — sixty-eight per cent of the force on the live database — and `pnpm expand:others`
 * creates the sections those officers actually carry as `UNIT` + `SECTION` siblings of the
 * `Others` unit they came out of. `Others` survives as the pen for what is still unmatched.
 *
 * ⚠️ NOT `OTHER`, WHICH WOULD MAKE "IS THIS A SECTION?" UNDECIDABLE. The commissionerate,
 * the ten territorial divisions and every `Others` unit already carry that placeholder, and
 * the ingest's sticky rule (`officerIngest.plan.ts`) must be able to ask "is this officer's
 * unit one of the division's sections?" with a column rather than a string match. NOT `PS`
 * either: the ingest would then resolve a section BY NAME as a police station, and report a
 * thousand stations with no section code.
 *
 * ⚠️ IT IS A CONVENTION, NOT A LOCK. The org screen's drawer offers every member, so a unit
 * an operator sets to `SECTION` by hand is treated exactly as an expanded one — and one set
 * AWAY from it releases its officers on the next ingest. `docs/ops/others-expansion.md` says so.
 */
export const ORG_UNIT_SUBTYPES = [
  'PS',
  'WOMEN_PS',
  'TRAFFIC_GUARD',
  'RESERVE_FORCE',
  'BATTALION',
  'WING',
  'SECTION',
  'OTHER',
] as const;
export type OrgUnitSubType = (typeof ORG_UNIT_SUBTYPES)[number];

/**
 * [amended INGEST] The two sub-types that ARE a police station.
 *
 * Exported rather than written as `subType === 'PS' || subType === 'WOMEN_PS'` at each
 * site: the ingest's resolver, its report and the establishment planner all ask this
 * question, and three inline disjunctions are three places to forget the second half the
 * day a third kind of station appears.
 */
export const POLICE_STATION_SUBTYPES = ['PS', 'WOMEN_PS'] as const satisfies readonly OrgUnitSubType[];
export const isPoliceStationSubType = (subType: OrgUnitSubType): boolean =>
  (POLICE_STATION_SUBTYPES as readonly OrgUnitSubType[]).includes(subType);

/**
 * ─── [amended WINGS.1] WHICH DIVISIONS ARE THE TERRITORIAL ("PRIMARY") TEN ────────
 *
 * The commissionerate's own ten — Central, North, Port — are the divisions an operator
 * reaches for daily; the twenty-six wings are the rest. Two places need the distinction
 * and they must not disagree: the DRO rule (a reserve office stands behind STATIONS, so a
 * division with none has no use for one) and the ORDERING rule (the ten sort first in
 * every list of divisions). They are one fact, so this is one array.
 *
 * ⚠️ IT TAKES `type` AS WELL, AND THAT HALF IS LOAD-BEARING. `WING` is the only sub-type
 * that answers "not territorial", so a bare `subType !== 'WING'` is true of EVERY OTHER NODE
 * in the collection: the commissionerate and the Others units by carrying `'OTHER'` (the
 * schema DEFAULT, `orgUnit.model.ts`), and all seventy-eight police stations by carrying
 * `PS`/`WOMEN_PS`, which is not `WING` either. A predicate that answers "yes" for a police
 * station is not a predicate about divisions, and the bug it produces — stations sorting
 * into a band reserved for divisions — is invisible until somebody looks at a tree.
 *
 * ⚠️ AN EXCLUSION, NEVER AN ALLOW-LIST — `DIVISION_UNIT_KINDS.notForDivisionSubTypes`'
 * argument in `api/src/seed/divisionUnits.seed.ts`, and this is the same list. A division
 * sub-type invented later joins the PRIMARY band by default and a reviewer has to decide
 * to take it out; an allow-list fails the other way, silently dropping the new sub-type to
 * the bottom of every picker, which looks exactly like the ordering working.
 *
 * ⚠️ NOTHING SORTS ON THE ENUM'S SPELLING, AND THIS EXISTS PARTLY TO STOP THAT.
 * `'OTHER' < 'WING'` is true, so `sort({ sub_type: 1 })` would separate the two bands TODAY
 * by pure accident of the alphabet. It is an accident: it says nothing about which band comes
 * FIRST beyond the letters, it puts `BATTALION`, `PS`, `RESERVE_FORCE` and `TRAFFIC_GUARD`
 * between them, and it inverts the moment a second non-territorial sub-type is spelled
 * before `'OTHER'` — `'BRANCH'`, say. Reordering `ORG_UNIT_SUBTYPES` changes nothing, because
 * a string sort reads the value and not the tuple; that is precisely why the coincidence is
 * so easy to rely on and so hard to see break. The order is expressed here or not at all.
 *
 * ⚠️ IT MUST TOLERATE A VALUE OUTSIDE THE ENUM. `.includes` on a non-member is `false`, so
 * an unrecognised or absent sub-type lands in the PRIMARY band rather than throwing — which
 * matters because org-unit payloads are built in several places and at least one web test
 * fixture carries `subType: null`. Falling into the default band is the same failure the
 * exclusion list already chooses.
 */
export const NON_TERRITORIAL_DIVISION_SUBTYPES = ['WING'] as const satisfies readonly OrgUnitSubType[];

export const isTerritorialDivision = (type: OrgUnitType, subType: OrgUnitSubType): boolean =>
  type === 'DIVISION' && !(NON_TERRITORIAL_DIVISION_SUBTYPES as readonly OrgUnitSubType[]).includes(subType);

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5 §6] AN OFFICER'S POSTING, AS ONE STRING — `Central Division · Nadial`.
 *
 *  `Nadial` alone does not say whose it is. A printed order lists officers from every division
 *  that was asked and a roster on a phone lists the force of one duty, so both need the pair —
 *  and §6's rule is that the pair is SENT rather than derived, because two clients deriving an
 *  ancestor separately will disagree. This is the join, in one place, so they cannot even
 *  disagree about the separator.
 *
 *  ⚠️ IT LIVES IN `constants/`, WHICH IS THE ONLY BARREL `pwa` MAY IMPORT AT RUNTIME. §8:
 *  everything else in `@pams/shared` drags zod or luxon into a 150 KB budget. That is also why
 *  `web/src/lib/addressee.ts` — which does the same job for a SOURCE — could not simply be
 *  reused: the PWA structurally cannot see it.
 *
 *  ⚠️ ` · `, THE SAME SEPARATOR `AddresseeCodes` ALREADY USES for a source. One product, one
 *  punctuation mark for "this, inside that".
 *
 *  ⚠️ AND IT DEGRADES RATHER THAN REFUSING. A unit outside any division — the commissionerate's
 *  own staff — answers with the unit alone; an officer whose unit could not be resolved answers
 *  `null`, which every caller already had to render for `orgUnitName`. A thrown error here
 *  would take down a printed order over a missing display string.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const postingLabel = (officer: {
  orgUnitName: string | null;
  orgUnitDivisionName: string | null;
}): string | null => {
  if (officer.orgUnitName === null) return officer.orgUnitDivisionName;
  if (officer.orgUnitDivisionName === null) return officer.orgUnitName;
  /* An officer posted TO a division: `loadDivisionsForOrgUnits` answers with that division
     itself, so both halves are the same string and saying it twice reads as a mistake. */
  if (officer.orgUnitDivisionName === officer.orgUnitName) return officer.orgUnitName;
  return `${officer.orgUnitDivisionName} · ${officer.orgUnitName}`;
};

// ── Ranks ────────────────────────────────────────────────────────────────────────

/**
 * From Appendix A's `Level` column. Appendix A has no `category` column — this IS that
 * column, transliterated to SCREAMING_SNAKE. `isGazetted` is not in Appendix A at all
 * and is derived; see `api/src/seed/rank.seed.ts` for the derivation and its caveat.
 */
export const RANK_CATEGORIES = ['COMMISSIONERATE', 'DIVISION', 'UNIT', 'ARMED_UNIT'] as const;
export type RankCategory = (typeof RANK_CATEGORIES)[number];

// ── Access ───────────────────────────────────────────────────────────────────────

/**
 * ONE field on the officer, not a set of booleans.
 *
 * `NONE` is the default and means mobile/officer-surface access only — it is the
 * "not nodal" of PA_REQUIREMENTS FR-3.2, so "flagged nodal" reads as
 * `accessRole !== 'NONE'`. The document's "System admin, scope Global" is SUPER_ADMIN.
 *
 * The CP-office-vs-Division distinction in §3.1's actor matrix is SCOPE — which org
 * node you sit on — not role. That is precisely what lets the scoping middleware be
 * the only thing that varies between two nodal admins.
 */
export const ACCESS_ROLES = ['SUPER_ADMIN', 'NODAL_ADMIN', 'NODAL_READ', 'NODAL_MONITOR', 'NONE'] as const;
export type AccessRole = (typeof ACCESS_ROLES)[number];

/** FR-5.8.20 — a dashboard token is issued to these and to nobody else. */
export const DASHBOARD_ACCESS_ROLES = ['SUPER_ADMIN', 'NODAL_ADMIN', 'NODAL_READ', 'NODAL_MONITOR'] as const;
export type DashboardAccessRole = (typeof DASHBOARD_ACCESS_ROLES)[number];

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C4a §8] WHAT A NODAL ROLE IS CALLED DEPENDS ON THE NODE — `(accessRole, orgUnitType)`.
 *
 *  `HQ Admin` at the commissionerate, `Division Admin` at a division, `PS Admin` at a unit — the
 *  pairing [P6a]'s dashboard layouts and [C1e §5a]'s file gate already key on. This is the ONE
 *  derivation of it; each app supplies the WORDS (`web/src/lib/labels.ts`, `pwa`'s catalogue).
 *
 *  ⚠️ PRESENTATION ONLY. It returns a level and the role it qualifies, and nothing reads it for a
 *  decision: no capability, route, scope or query changes, and the stored `access_role` values are
 *  untouched. A label that changed an identifier would be a migration, not a rename.
 *
 *  ⚠️ `SUPER_ADMIN` AND `NONE` ARE NOT LEVELLED, and neither is a nodal with no org unit — those
 *  answer `null`, which every caller renders as the unchanged label.
 *
 *  ⚠️ BY TYPE, NOT BY SUB-TYPE, as instructed: a `UNIT` that is not a police station still reads
 *  `PS`, and a non-territorial wing still reads `Division`. Stated rather than special-cased.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const NODAL_LEVELS = ['HQ', 'DIVISION', 'PS'] as const;
export type NodalLevel = (typeof NODAL_LEVELS)[number];

export const LEVELLED_ACCESS_ROLES = ['NODAL_ADMIN', 'NODAL_MONITOR', 'NODAL_READ'] as const;
export type LevelledAccessRole = (typeof LEVELLED_ACCESS_ROLES)[number];

export const NODAL_LEVEL_BY_ORG_UNIT_TYPE = {
  COMMISSIONERATE: 'HQ',
  DIVISION: 'DIVISION',
  UNIT: 'PS',
} as const satisfies Record<OrgUnitType, NodalLevel>;

export const nodalLevelOf = (
  role: AccessRole,
  orgUnitType: OrgUnitType | null | undefined,
): { level: NodalLevel; role: LevelledAccessRole } | null => {
  if (orgUnitType === null || orgUnitType === undefined) return null;
  const levelled = LEVELLED_ACCESS_ROLES.find((candidate) => candidate === role);
  return levelled === undefined ? null : { level: NODAL_LEVEL_BY_ORG_UNIT_TYPE[orgUnitType], role: levelled };
};

/**
 * Used for two questions, and the first is: may this actor grant that role?
 *
 * Strictly greater, so a NODAL_ADMIN cannot mint a peer. This is NOT a supervisor hierarchy —
 * PA_REQUIREMENTS §3.2's trap warns against deriving command authority from rank order, and
 * this is not that: it is an administrative grant check over system roles, not over police ranks.
 *
 * ⚠️ [amended P7A §7] "ONLY A SUPER_ADMIN CAN MINT A SUPER_ADMIN" WAS FALSE, AND THE TRUE SENTENCE
 * IS STRONGER: NOBODY CAN, THROUGH THE API. The check is strictly greater and nothing outranks
 * SUPER_ADMIN, so the only way a super admin comes to exist is `pnpm seed:superadmin` (or the
 * database). `master_superadmin` below changes none of that — it lets a holder ACT ON another
 * super admin, never promote anyone to one.
 *
 * ⚠️ [amended P7A §7] AND IT HAS ANSWERED A SECOND QUESTION SINCE [C5b review], WHICH THE LINE ABOVE
 * NEVER SAID: in `officer.service.ts`' `assertMayAdminister`, may this actor WRITE to an officer who
 * already holds a role above their own. Same table, the strictness rule read the other way.
 */
export const ACCESS_ROLE_RANK = {
  SUPER_ADMIN: 100,
  NODAL_ADMIN: 60,
  NODAL_READ: 40,
  NODAL_MONITOR: 30,
  NONE: 0,
} as const satisfies Record<AccessRole, number>;

/**
 * Authorisation is DATA-DRIVEN AND FAILS CLOSED (CLAUDE.md §10). No role `switch`
 * ladders anywhere, on either side of the wire.
 *
 * A capability added here without a role listed grants nothing; a role added to
 * `ACCESS_ROLES` and forgotten here gets nothing. Both failures are in the safe
 * direction, which is the entire reason this is a table and not a function.
 *
 * The web reads it to decide what to disable. **That is not enforcement** — the API
 * checks the same table, and subtree scoping is enforced in the query itself.
 */
export const CAPABILITIES = {
  'dashboard.access': ['SUPER_ADMIN', 'NODAL_ADMIN', 'NODAL_READ', 'NODAL_MONITOR'],
  /**
   * ⚠️ [P6b] SPLIT FROM `dashboard.access`, AND IT IS A STRICTLY NARROWER GRANT — which is
   * the only kind of split this table permits.
   *
   * The rule three entries below says a capability always granted alongside another grants
   * nothing, and it is why there is no `dashboard.read`. This is the opposite case:
   * `dashboard.access` is held by ALL FOUR dashboard roles, so a configurator gated on it
   * would let a NODAL_MONITOR — whose entire point is that they change nothing — rewrite
   * what every nodal in the commissionerate opens on every morning. `policy.publish` split
   * from `policy.write` is the precedent for the shape, and this is the stronger case: that
   * one kept the same role set so a later pass could widen one half, this one narrows four
   * roles to one on the day it is minted.
   *
   * ⚠️ AND IT IS NOT NAMED `dashboard.write`, BECAUSE WHAT IT GATES IS NOT THE HOLDER'S OWN
   * SCREEN. A layout is keyed on `(accessRole, orgUnitType)`, and `resolveViewer` forces a
   * super admin's GLOBAL scope to COMMISSIONERATE — so the holder of this capability
   * structurally cannot open eleven of the twelve surfaces they can change. That is
   * `org.reparent`'s blast radius with `org.reparent`'s answer, and it is also why P6b ships
   * a structure-only preview rather than an impersonation path.
   */
  'dashboard.configure': ['SUPER_ADMIN'],
  /**
   * ⚠️ [C1h] THE SYSTEM-WIDE SETTINGS STORE. SUPER_ADMIN, AND THE COST IS STATED HERE.
   *
   * It is granted to EXACTLY the roles `dashboard.configure` already holds, which is the
   * shape the rule below refuses — a capability always granted alongside another grants
   * nothing, and that is why there is no `dashboard.read`. Minted anyway, deliberately, and
   * the reason is what it GATES rather than who holds it: `dashboard.configure` means "may
   * rearrange the tiles", and its own header above spends a paragraph on why it is not even
   * called `dashboard.write`. A settings store is not dashboard tile configuration, and
   * `setting.constant.ts` is explicit that adding key #2 must be a WRITE rather than a
   * migration — so gating a future retention window or purge cadence on a capability named
   * after tiles is exactly the drift this table exists to prevent.
   *
   * ⚠️ THE ACCEPTED COST, SAID OUT LOUD: today the two are indistinguishable in effect, so
   * this buys nothing until the second key exists. `ranks.is_command_eligible` states its
   * own cost the same way — the day a settings key wants a different grant it splits in that
   * commit, and there is something for it to split FROM.
   */
  'settings.write': ['SUPER_ADMIN'],
  'rank.read': ['SUPER_ADMIN', 'NODAL_ADMIN', 'NODAL_READ', 'NODAL_MONITOR'],
  'org.read': ['SUPER_ADMIN', 'NODAL_ADMIN', 'NODAL_READ', 'NODAL_MONITOR'],
  'org.write': ['SUPER_ADMIN', 'NODAL_ADMIN'],
  /**
   * SUPER_ADMIN only. Reparenting rewrites the `ancestors` array of every descendant
   * and therefore changes WHO CAN SEE WHOM (FR-3.4, FR-3.6). That is not a routine
   * edit and it is not a divisional nodal's call to make.
   */
  'org.reparent': ['SUPER_ADMIN'],
  /**
   * ⚠️ [C5c §11d] SUPER_ADMIN ONLY, AND ELEVATION CANNOT REACH IT — "the one control that grants
   * reach must not be reachable by the thing it grants". An elevated nodal admin acts as a
   * commissionerate NODAL_ADMIN, and no nodal role holds this key at any level, so raising their own
   * unit, another division's, or their ancestor's is a 403 at the route before any read.
   *
   * ⚠️ ITS OWN KEY, NOT `org.reparent` BORROWED, though the role set is identical: both change who
   * can see whom, but a reparent moves a node and this moves a reach — `settings.write`'s ground.
   */
  'org.elevate': ['SUPER_ADMIN'],
  'officer.read': ['SUPER_ADMIN', 'NODAL_ADMIN', 'NODAL_READ', 'NODAL_MONITOR'],
  'officer.write': ['SUPER_ADMIN', 'NODAL_ADMIN'],
  'officer.transfer': ['SUPER_ADMIN', 'NODAL_ADMIN'],
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [P7A §7] THE ROLE BASELINE FOR WRITING TO **ANOTHER** SUPER ADMIN'S RECORD — AND NOTHING
   *  GATES ON IT ALONE.
   *
   *  It is the role half of `SUPER_ADMIN_ADMINISTRATION` below; the other half is the
   *  `master_superadmin` flag. `officer.service.ts`' `assertMayAdminister` asks for both before an
   *  edit (phone included), a transfer, a deactivation or a role change lands on a super admin who
   *  is not the caller. No route, page or nav row names this key by itself.
   *
   *  ⚠️ MINTED THOUGH ITS ROLE SET IS IDENTICAL TO `org.reparent`'s, ON `analytics.read`'S GROUND:
   *  what it GATES is a different subject. A requirement paired with `officer.write` instead would
   *  let a NODAL_ADMIN holding the flag pass the role half — a flag can only ADD, and the role row is
   *  what keeps a flagged nodal refused. Naming SUPER_ADMIN alone here is that refusal, and
   *  `identity.constant.test.ts` holds it as a truth table.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  'officer.writeSuperAdmin': ['SUPER_ADMIN'],
  /**
   * ⚠️ [amended P1b] THE PLACE MASTER IS GLOBAL, AND `place.read` IS THE PROOF.
   *
   * Every dashboard role, with no scope narrowing anywhere behind it. That is a
   * deliberate departure from every other read in this table, and it is the whole
   * design: a shared gazetteer that fragments per division is not a gazetteer, and
   * local-first autocomplete only ever gets good if everybody feeds the same one.
   *
   * A PLACE EXISTING IS NOT SENSITIVE. A police arrangement AT that place is, and that
   * isolation lives on the arrangement (FR-3.6), not here. Anyone tempted to "fix"
   * this by adding a scope should read `services/place.service.ts`'s header first.
   */
  'place.read': ['SUPER_ADMIN', 'NODAL_ADMIN', 'NODAL_READ', 'NODAL_MONITOR'],
  'place.write': ['SUPER_ADMIN', 'NODAL_ADMIN'],
  /**
   * SUPER_ADMIN only, on the same argument as `org.reparent`. Merging rewrites what
   * every future reference to the absorbed place resolves to, across the whole
   * commissionerate — it is not a routine edit, and it cannot be undone by editing a
   * field back.
   */
  'place.merge': ['SUPER_ADMIN'],
  /**
   * [P2] Post types, vehicle types and policies are GLOBAL CONFIGURATION, readable by
   * every dashboard role and writable only by SUPER_ADMIN.
   *
   * The read grant is the same shape as `place.read` and is NOT the same claim. Places
   * are global because a gazetteer that fragments is not a gazetteer; these are global
   * because they have no owning org node at all — there is no scope column behind any of
   * them to narrow by. A nodal who cannot read the policy cannot read the P3 arrangement
   * that references it, or understand what a post column means when filling one in.
   *
   * The write grant is DELIBERATELY NARROWER than `org.write` and `place.write`. A
   * policy change reshapes every future arrangement across the whole commissionerate —
   * that is `org.reparent` and `place.merge` blast radius, not routine editing.
   *
   * ⚠️ [amended C5c §3] STILL TRUE OF POST TYPES AND VEHICLE TYPES, AND NO LONGER OF POLICIES.
   * A client decision, reversing [P2] with the cost stated at the three policy rows below: a
   * NODAL_ADMIN seated at the COMMISSIONERATE ROOT may now create, update, publish and discard
   * a policy draft. The blast-radius argument above is not refuted — it is why the grant stops
   * at the CP office's own desk, why archive, restore and the default stay SUPER_ADMIN under
   * `policy.configure`, and why the vocabulary masters beside it are untouched.
   */
  'postType.read': ['SUPER_ADMIN', 'NODAL_ADMIN', 'NODAL_READ', 'NODAL_MONITOR'],
  'postType.write': ['SUPER_ADMIN'],
  'vehicleType.read': ['SUPER_ADMIN', 'NODAL_ADMIN', 'NODAL_READ', 'NODAL_MONITOR'],
  'vehicleType.write': ['SUPER_ADMIN'],
  'policy.read': ['SUPER_ADMIN', 'NODAL_ADMIN', 'NODAL_READ', 'NODAL_MONITOR'],
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C5c §3] WIDENED TO NODAL_ADMIN, AND THE REAL NARROWING MOVED INTO THE SERVICE.
   *
   *  The client asked that a NODAL_ADMIN at the commissionerate may CREATE, UPDATE, PUBLISH and
   *  DISCARD a policy draft, and that a DIVISION nodal admin — the same role — may not (§3d). No
   *  capability can say that: `scopeForActor` branches on `SUPER_ADMIN` alone, `Scope` carries no
   *  org unit type, and a `NODAL_ADMIN` at a police station holds exactly what one at the CP
   *  office holds. So the grant widens to the ROLE and `assertMayAuthorPolicy`
   *  (`policy.service.ts`) refuses everyone not seated at the ROOT, by POSITION through
   *  `isSeatedAtCommissionerate` and never by the `type` label a divisional nodal can rewrite
   *  ([C1e §5]). [LIFECYCLE]'s "the grant widens and the real limits move into the SERVICE".
   *
   *  ⚠️ NOT A NEW `policy.author` KEY. It would hold exactly `arrangement.write`'s role set, and
   *  a capability always granted alongside another grants nothing — worse, it would READ as
   *  though every nodal admin may author, when the real limit is a node this table cannot name.
   *
   *  ⚠️ AND IT NO LONGER GATES ARCHIVE, RESTORE OR THE DEFAULT. Widening this key alone would
   *  have handed all three to every nodal admin at the route — a widening that quietly re-grants
   *  verbs nobody asked for, which is [LIFECYCLE]'s "a rename must never re-grant" read the
   *  other way. They moved to `policy.configure` below in the same change.
   *
   *  ⚠️ THE REACH, STATED (§3e). This is not the first nodal write on a global entity —
   *  `place.write` is — but it is the first on a VERSIONED master whose current version every
   *  new order binds to: an HQ publish moves what every division is offered, and a rename
   *  relabels issued orders, because a policy's name is read fresh rather than snapshotted.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  'policy.write': ['SUPER_ADMIN', 'NODAL_ADMIN'],
  /**
   * Split from `policy.write` on the `place.merge` argument: publishing FREEZES a
   * version for ever and cannot be undone by editing a field back. The split also means
   * a later pass can widen `policy.write` to NODAL_ADMIN without widening publish, which
   * is the direction this is likely to move.
   *
   * ⚠️ [amended C5c §3] BOTH HALVES WIDENED TOGETHER, because the client named publish among
   * the four verbs — and behind the same seat guard, so a divisional nodal still cannot. The
   * split SURVIVES the identical grant deliberately: it is what lets a later pass narrow one
   * half without the other, which is the only reason it was ever made.
   */
  'policy.publish': ['SUPER_ADMIN', 'NODAL_ADMIN'],
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C5c §3] ARCHIVE, RESTORE AND THE DEFAULT POLICY — SUPER_ADMIN, A STRICTLY NARROWER
   *  SPLIT FROM `policy.write`, WHICH IS THE ONLY KIND THIS TABLE PERMITS.
   *
   *  `dashboard.configure`'s rule at the top of this table: a split is allowed when it NARROWS.
   *  Archive is a policy's "delete" ([C2b §8e] ruled hard delete a deliberate non-build), and
   *  §3b says delete stays with SUPER_ADMIN; restore is archive run backwards, and the two halves
   *  of one decision sit with one person ([LIFECYCLE]); and moving the default is not among the
   *  four verbs the client named, so it fails CLOSED here and the CHANGELOG asks them to confirm.
   *  It also gates `createPolicy` with `isDefault: true` — the one configure act that arrives in a
   *  request BODY, read in the service because a route cannot see a body.
   *
   *  ⚠️ ITS OWN KEY, NOT `settings.write` OR `lifecycle.archivePublished` BORROWED. Its role set
   *  is identical to theirs today, which is the shape "a capability granted alongside another
   *  grants nothing" refuses — minted anyway on `settings.write`'s ground: what it GATES is a
   *  different subject, and a policy archive gated on a key named for the settings store is the
   *  cross-subject drift that key's own header warns about.
   *
   *  ⚠️ A ROLE-ONLY NARROWING, SO IT IS ENFORCED AT THE ROUTE — "super admin or nobody" is exactly
   *  what `dashboardStack` expresses, before validation and before any read, so its 403 cannot
   *  depend on the id. The SEAT is what the service is for; this is not a seat.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  'policy.configure': ['SUPER_ADMIN'],
  /**
   * ⚠️ [P3a] THE ARRANGEMENT IS THE FIRST ENTITY IN THIS TABLE WHOSE READ GRANT IS NOT
   * THE WHOLE STORY, AND THAT IS THE POINT.
   *
   * Every dashboard role holds `arrangement.read`, and it grants almost nothing on its
   * own: FR-3.6 says a nodal sees a PA only if their scope holds an allocation on it, and
   * that is enforced in the QUERY, by `arrangementScopeFilter` in
   * `api/src/services/arrangement.service.ts`. Two arrangements at the same place and the
   * same hour, addressed to different divisions, are mutually invisible — deliberately,
   * as an operational feature rather than a side effect. This capability answers "may
   * they open the screen at all"; the filter answers "which rows exist for them".
   *
   * That is also why an out-of-scope by-id read returns 404 rather than 403: a 403 on a
   * real id beside a 404 on a fake one is an existence oracle, which is exactly the leak
   * FR-3.6 exists to prevent.
   */
  'arrangement.read': ['SUPER_ADMIN', 'NODAL_ADMIN', 'NODAL_READ', 'NODAL_MONITOR'],
  /**
   * WIDER than `policy.write`, and narrower than it looks.
   *
   * ⚠️ [amended C5c §3] THE SAME ROLE SET AS `policy.write` NOW, AND STILL THE WIDER GRANT IN
   * EFFECT: a divisional nodal authors arrangements (subject to `arrangement.nodalAuthor`) and
   * never policies, because `assertMayAuthorPolicy` refuses every seat below the root.
   *
   * Raising a bandobast is the CP office's daily work, not a configuration change, so a
   * nodal admin must be able to do it — a demo in which only a super admin can use the
   * product's main screen is a demo of the wrong product. A DIVISIONAL nodal admin holds
   * this too, and the scope filter is what keeps their arrangements theirs.
   */
  'arrangement.write': ['SUPER_ADMIN', 'NODAL_ADMIN'],
  /**
   * Split from `arrangement.write` on the `policy.publish` argument, and kept at the same
   * grant for now.
   *
   * Publishing is the irreversible half: it generates every duty, freezes the matrix
   * against further edits (FR-5.2.14), and in P4 it is what fans work out to the
   * divisions and fires SMS. The split exists so a later pass can narrow publishing
   * without narrowing drafting — which is the direction this is more likely to move.
   */
  'arrangement.publish': ['SUPER_ADMIN', 'NODAL_ADMIN'],
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C3c.2] COMPLETE WITH SHORT-FALL — AND IT IS THE FIRST WRITE A `NODAL_MONITOR` HOLDS.
   *
   *  A division that was asked for ten U/A men and can supply seven closes its share at seven
   *  WITH A REASON (`allocation_fills.shortfall_reason`). [P4] built that path,
   *  [C3c.1 §6a] removed the only control that reached it, and the client has asked for it
   *  back — on a wider audience than the one it had.
   *
   *  ⚠️ IT IS MINTED THOUGH THIS TABLE USUALLY REFUSES A NEW KEY, AND THE RULE IS WHAT LICENSES
   *  IT. The standing objection — "a capability always granted alongside another grants
   *  nothing", which is why there is no `fulfilment.write`, no `arrangementGroup.read` and no
   *  `dashboard.read` — does not apply: `NODAL_MONITOR` holds this and holds NO other write key
   *  in the table, so the grant list `{SUPER_ADMIN, NODAL_ADMIN, NODAL_MONITOR}` is one no
   *  existing capability expresses and no conjunction of them produces. Every one of that role's
   *  ten current keys is co-extensive with `NODAL_READ`'s, so `arrangement.read` cannot say it
   *  either — it would additionally hand the act to `NODAL_READ`, whom the client did not name.
   *
   *  ⚠️⚠️ AND IT REVERSES AN INVARIANT THIS FILE STATES IN TERMS, TWENTY LINES ABOVE
   *  (`dashboard.configure`): "a NODAL_MONITOR — whose entire point is that they change
   *  nothing". That sentence is now false, and the cost is stated here rather than left for the
   *  next reader to discover: a monitor can close another unit's share at partial, permanently,
   *  and the only trace is the `ALLOCATION_COMPLETED` audit row. It is a CLIENT DECISION, taken
   *  with the trade named, exactly as [P4-UX.1]'s enumeration oracle and [C1e §5]'s filing tier
   *  were. Reversing it back is this one line.
   *
   *  ⚠️ IT IS A `fulfilment.` KEY AND NOT AN `arrangement.` ONE, BECAUSE THE PREFIX IS THE
   *  SIGNAL. Every `arrangement.*` write is `['SUPER_ADMIN','NODAL_ADMIN']`, and a key filed
   *  among them whose grant list differs is the one a reader skims past.
   *
   *  ⚠️ AND THE CAPABILITY IS NOT THE CONTROL. It answers "may they open this at all"; FR-3.6 is
   *  `requireDuty` refusing another division's duty with a 404, and the DIVISION-level narrowing
   *  the client asked for is a NODE — which no capability can carry ([C1c §7]) and which
   *  `web/src/lib/accessRole.ts` therefore expresses as an OFFER gate. Deliberately NOT enforced
   *  on the server: doing so would REMOVE an act a station-seated `NODAL_ADMIN` holds today
   *  through `arrangement.write`, and [LIFECYCLE]'s rule is that a rename must never re-grant —
   *  its mirror image being that a widening must never quietly narrow.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  'fulfilment.completeShortfall': ['SUPER_ADMIN', 'NODAL_ADMIN', 'NODAL_MONITOR'],
  /**
   * ⚠️ [LIFECYCLE] WIDENED TO NODAL_ADMIN, AND THE NARROWING MOVED RATHER THAN VANISHED.
   *
   * It read SUPER_ADMIN only, on the `place.merge` argument. That was the only narrowing a
   * ROUTE can express — "super admin or nobody" — and §2 asks for something a route cannot
   * say: a nodal may withdraw a DRAFT within their own scope, and nothing published, ever.
   *
   * So the grant widens and the real limits move into the SERVICE, where they can be
   * stated precisely: `assertMayArchive` refuses anything not a draft without
   * `lifecycle.archivePublished`, and `Scope` — which is what separates a CP-office nodal
   * from a divisional one (see this file's own note at the top) — decides which orders
   * exist for them at all, with the same 404-not-403 consequence.
   *
   * ⚠️ THE CONSEQUENCE, STATED: a DIVISIONAL nodal admin can now archive a draft they
   * raised. That follows from the ruling that CP-office-versus-Division is scope rather
   * than role.
   *
   * ⚠️ [amended C1e §5] THIS LINE USED TO END "if the client wants a third tier it is a new
   * `AccessRole`, not a predicate smuggled in under an archive feature." THE CLIENT ASKED
   * FOR THE THIRD TIER AND DID NOT ASK FOR A ROLE, SO THE RULING IS REVERSED — confirmed,
   * and recorded here rather than in a changelog nobody re-reads.
   *
   * What was built is the predicate, and three things bound it:
   *
   *  • It is **FILING only** — an order every duty of which has ENDED. Filing tells nobody,
   *    writes no `CANCELLED` and emits no event. `CANCELLATION` — withdrawing an order that
   *    has not run — is untouched and still needs `lifecycle.archivePublished` below.
   *  • It is **NOT a new role and NOT a capability.** A `lifecycle.filePublished` would hold
   *    exactly the roles `arrangement.delete` holds, which is the "granted alongside another
   *    grants nothing" rule stated one entry down — and it would READ as though every nodal
   *    admin could file, when the real narrowing is a node it cannot express.
   *  • The narrowing is `(accessRole, orgUnitType)`, the pairing [P6a] already keys every
   *    dashboard layout on. It lives in `assertMayArchive`, which is where this file's own
   *    argument says a question about a role AND a node AND a record belongs.
   *
   * Archive and restore stay on ONE grant, unchanged: the two halves of one decision should
   * not sit with different people — and restore asks the same question backwards, decoding
   * which act was taken from the record itself (`archivedTierOf`).
   */
  'arrangement.delete': ['SUPER_ADMIN', 'NODAL_ADMIN'],
  /**
   * ⚠️ [LIFECYCLE] ONE CAPABILITY CARRYING ONE BIT: may this actor act on a record that is
   * NOT a draft?
   *
   * Everything else about archiving is already carried by the entity's own capability plus
   * `Scope` plus the record's own columns, so this is the only thing left that a table can
   * answer. It gates archive AND restore of an issued order — restoring a cancelled
   * bandobast puts it back in front of divisions, which is the same decision run backwards.
   *
   * ⚠️ AND THERE IS DELIBERATELY NO `lifecycle.hardDelete`. After the widening above it
   * would be granted to exactly the roles that hold `arrangement.delete`, and a capability
   * always granted alongside another grants nothing — the argument that already keeps
   * `arrangementGroup.read` from existing. Hard delete is narrowed by STATE instead
   * (`assertMayHardDelete`), which no grant can widen.
   */
  'lifecycle.archivePublished': ['SUPER_ADMIN'],
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [P7B §6] THE ROLE HALF OF THE TWO `master_superadmin` ARRANGEMENT OVERRIDES — bulk draft
   *  discard, and taking a RUNNING order back to draft. SUPER_ADMIN alone, and nothing gates on it
   *  ALONE: `BULK_DRAFT_DISCARD` and `REVERT_RUNNING_ARRANGEMENT` below pair it with a flag capability
   *  `master_superadmin` grants, so a super admin without the flag is refused, and a nodal admin who
   *  was given the flag by the seed tool is refused on this row.
   *
   *  ⚠️ FILED UNDER `lifecycle.`, NEVER `arrangement.`: every `arrangement.*` write above is
   *  SUPER_ADMIN + NODAL_ADMIN, and a key with another grant filed among them reads as one of them.
   *
   *  ⚠️ THE COST, STATED ON `settings.write`'S GROUND: it is granted to exactly the roles
   *  `lifecycle.archivePublished` holds, which this table's rule refuses. Minted anyway because what it
   *  gates is a different act — piercing [C1d]'s refusal of a running order — and the day either
   *  needs another grant it splits in that commit, with something to split FROM.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  'lifecycle.override': ['SUPER_ADMIN'],
  /**
   * [P3b.1] Round-the-clock standing orders. `read` follows the `arrangement.read`
   * argument verbatim — it answers "may they open the screen", and FR-3.6 in the query
   * answers "which rows exist", with the same 404-not-403 consequence.
   */
  'standingOrder.read': ['SUPER_ADMIN', 'NODAL_ADMIN', 'NODAL_READ', 'NODAL_MONITOR'],
  /** The `arrangement.write` argument: a standing naka is the CP office's daily work. */
  'standingOrder.write': ['SUPER_ADMIN', 'NODAL_ADMIN'],
  /**
   * ⚠️ SPLIT FROM `write`, ON A STRONGER ARGUMENT THAN SYMMETRY: THERE IS NO UN-END.
   *
   * Reviving a standing order after N days would leave a hole in the generated series that
   * nothing backfills, so "resume" is a new standing order. An act with no inverse is not a
   * routine edit, and the split lets a later pass narrow it without narrowing the edit.
   */
  'standingOrder.end': ['SUPER_ADMIN', 'NODAL_ADMIN'],
  /**
   * [P3b.1] Manual grouping. ⚠️ THERE IS DELIBERATELY NO `arrangementGroup.read`: a group
   * is only ever reached through an arrangement, so it is gated by `arrangement.read`, and
   * a capability always granted alongside another grants nothing.
   */
  'arrangementGroup.write': ['SUPER_ADMIN', 'NODAL_ADMIN'],

  /**
   * SUPER_ADMIN only. The audit log records who did what, including to whom.
   *
   * [amended C5b §5] This line sat four entries up, above `standingOrder.read`, for several passes;
   * it is moved here, beside the capability it describes, and nothing else about it changed.
   *
   * ⚠️ [amended AUDIT-FLAG] AND NOTHING GATES ON THIS KEY ALONE ANY MORE — `AUDIT_ACCESS` BELOW.
   * A client decision, reversing [C5b §5]'s "the audit log is untouched by the flag": the API route,
   * the web route and the nav row take this capability AND the officer's own `access_analytics`
   * flag, which is the same one flag that opens usage analytics. The ROW is unchanged, which keeps
   * `can()` — and every one of its callers — meaning exactly what it meant, and is what makes this a
   * narrowing of who may READ rather than a change to who holds the capability.
   */
  'audit.read': ['SUPER_ADMIN'],
  /**
   * ⚠️ [ANALYTICS] USAGE ANALYTICS AND AN OFFICER'S LAST FIFTEEN. SUPER_ADMIN, AND THE COST IS
   * STATED HERE.
   *
   * It is granted to EXACTLY the roles `audit.read` holds, which is the shape this table's rule
   * refuses — a capability always granted alongside another grants nothing. Minted anyway, on
   * `settings.write`'s ground: what it GATES is a different subject. `audit.read` opens the record
   * of who changed what; this opens network telemetry — addresses, request volumes, refusal
   * clusters — and where named officers were standing. The day either wants a narrower or wider
   * grant it splits in that commit, and there is something for it to split FROM.
   *
   * ⚠️ [amended C5b §5] "TODAY THE TWO ARE INDISTINGUISHABLE IN EFFECT" IS NO LONGER TRUE, AND THIS
   * IS THE SPLIT THAT SENTENCE WAITED FOR. Nothing gates on this key ALONE any more: both analytics
   * routes, the web route and the nav row take `ANALYTICS_ACCESS` below — this capability AND the
   * officer's own `access_analytics` flag. A nodal admin holding the flag is still refused, because
   * the role row here does not name them. The row itself is unchanged, which keeps `can()` — and
   * every one of its callers — meaning exactly what it meant.
   *
   * ⚠️ [amended AUDIT-FLAG] INDISTINGUISHABLE AGAIN, AND SAID OUT LOUD RATHER THAN LEFT TO BE
   * NOTICED. `audit.read` now takes the same flag through `AUDIT_ACCESS`, so the two requirements
   * select EXACTLY the same population once more — one role row each, one `access_analytics` flag
   * granting both flag capabilities. **The minting argument is untouched**: what each gates is still
   * a different subject, and keeping them two values is precisely what makes "the audit log granted
   * separately from the telemetry" one row of `FLAG_GRANTS` rather than an edit at a gate.
   */
  'analytics.read': ['SUPER_ADMIN'],
} as const satisfies Record<string, readonly AccessRole[]>;

export type Capability = keyof typeof CAPABILITIES;

/**
 * ⚠️ [C5c §3] THE ONE SENTENCE FOR "YOU ARE NOT SEATED WHERE POLICIES ARE WRITTEN", read by both
 * sides of the wire: the API's `assertMayAuthorPolicy` refuses with it and the web's policy
 * screens show it as the disabled control's reason. Two copies of one refusal drift ([C3c.2]
 * #671), so there is one literal. It names the desk and what to do, never the caller — the
 * `DEACTIVATED` rule — and it says the READ is untouched, because §3 narrows nothing a nodal
 * could do before.
 */
export const POLICY_AUTHOR_REFUSAL =
  'Policies are written by the CP office. Ask them to make this change — every policy stays open to read.';

/**
 * The one place either side answers "may THIS ROLE?". Fails closed on an unknown role.
 *
 * ⚠️ [amended C5b §5] IT STAYS ABOUT THE ROLE, AND ONLY THE ROLE. A question about one OFFICER —
 * a role plus the flags on their record — is `meetsRequirement` below, which calls this for its
 * baseline. Nothing here reads a flag, so every existing caller (some forty on the web, the API's
 * services and the shared layout tables, all of which hold a bare role) means what it meant.
 */
export const can = (role: AccessRole, capability: Capability): boolean =>
  (CAPABILITIES[capability] as readonly AccessRole[]).includes(role);

// ── [C5b §5] Per-officer flags — grants, never denials ───────────────────────────

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5b §5] A FLAG ON AN OFFICER'S OWN RECORD, AND IT CAN ONLY ADD.
 *
 *  `officers.flags` holds zero or more of these. The first is `access_analytics`: usage analytics,
 *  an officer's last fifteen AND — [amended AUDIT-FLAG] — the audit log are opened by a super admin
 *  WHO HAS BEEN GRANTED IT, not by every super admin. A flag never takes anything away — there is no
 *  flag that denies, and `meetsRequirement` has no branch through which one could — so the only
 *  thing an officer loses by not holding one is the capabilities that flag would have added.
 *
 *  ⚠️ NO API ROUTE WRITES A FLAG. `createOfficerSchema` and `updateOfficerSchema` do not declare the
 *  key, so zod strips it; the only writer is the operator tool (`pnpm --filter @pams/api
 *  seed:officer-flag`), which writes the database directly. That is what makes it safe for an
 *  authorisation predicate to read the column at all: [C1e §5]'s rule is that a predicate may not
 *  read a column the role it narrows may write, and `officer.write` is SUPER_ADMIN and NODAL_ADMIN.
 *
 *  ⚠️ [amended C5b review] AND THE FLAG HOLDER'S SIGN-IN PHONE WAS WRITABLE, WHICH IS THE SAME
 *  QUESTION ONE COLUMN OVER. A CP-office NODAL_ADMIN could PATCH a super admin's `phone` to their own
 *  SIM and sign in as them, flag and all. `officer.service.ts`' `assertMayAdminister` now refuses any
 *  write to an officer who outranks the caller (update, transfer, deactivate). ⚠️ THE RESIDUAL,
 *  STATED: a super admin may still re-point ANOTHER super admin's phone — including a flag holder's —
 *  because nothing ranks above SUPER_ADMIN. The flag therefore holds against every role below it and
 *  against an honest super admin; the edit is attributable (`OFFICER_UPDATED`, `phone` in `changed`).
 *  `docs/ops/analytics.md` §5.1 states the same.
 *
 *  ⚠️ [amended P7A §7] THAT RESIDUAL IS NARROWED, BY THE SECOND FLAG. A super admin may now change
 *  ANOTHER super admin — edit (phone included), transfer, deactivate, change the role — only while
 *  holding `master_superadmin`, and never one who ALSO holds it: a holder's record is changed by the
 *  holder alone (or at the database). So re-pointing a flag holder's phone needs a holder, and
 *  re-pointing a HOLDER's phone needs the console. `docs/ops/analytics.md` §5.2.
 *
 *  ⚠️ [amended P7A §7] AND "A FLAG ONLY ADDS" IS A SENTENCE ABOUT THE **HOLDER**. Held by the TARGET,
 *  `master_superadmin` closes that target's record to every other officer — the one place in the
 *  system where a flag on somebody else's record narrows what YOU may do. The S ⊆ T property below
 *  is untouched (a caller holding more flags never loses a grant); what is new is that the answer to
 *  "may I write to X" now reads X's flags too, through the same resolver, in `assertMayAdminister`.
 *
 *  ⚠️ THE MODEL CARRIES NO `enum` FOR IT, AND THE READ IS WHERE AN UNKNOWN VALUE DIES. Mongoose
 *  re-validates a hydrated document's loaded paths on `save()`, so an enum would turn one value
 *  mistyped in `mongosh` into a throw on EVERY later save of that officer — `deactivateOfficer`
 *  included. `knownFlags` drops it instead, and an unknown value grants nothing.
 *
 *  ⚠️ [amended AUDIT-FLAG] AND THE STORED VALUE IS HISTORICAL — DELIBERATELY NOT RENAMED.
 *  `access_analytics` opens the audit log too, so the name UNDER-DESCRIBES what it grants, and that
 *  is the cheaper of the two costs: the value is the column, the CLI argument (`seed:officer-flag
 *  --flag access_analytics`), the mongosh block and the deploy step in `docs/ops/analytics.md` §5.1,
 *  so renaming it is a migration plus a CLI change plus a doc change plus a re-grant, to buy one
 *  word. Do not rename it — `FLAG_GRANTS` below is the one place that answers what it opens.
 *
 *  ⚠️ [P7A §7] THE SECOND IS `master_superadmin`: a SUPER_ADMIN holding it may change another super
 *  admin who does not. It grants nothing on any page or route — only `officer.service.ts` asks for
 *  it, because it is the one rule in the system about TWO officers (the caller and the target), and
 *  no route gate can see the target. It is set exactly like the first: `seed:officer-flag` or mongosh.
 *
 *  ⚠️ [amended P7B §6] "NOTHING ON ANY PAGE OR ROUTE" IS NO LONGER TRUE. It also grants the two
 *  arrangement overrides — bulk draft discard and taking a running order back to draft — through
 *  `BULK_DRAFT_DISCARD` and `REVERT_RUNNING_ARRANGEMENT` below, gated at the route and on the page.
 *
 *  ⚠️ [LOCATION-INTEL] THE THIRD IS `location_intel`: the Locational Intel map — where the officers in
 *  the holder's OWN scope last reported a location, and one officer's name, rank and posting on a
 *  click. Its role half is `officer.read`, so unlike the first two it is not a SUPER_ADMIN-only door:
 *  a division nodal holding it sees their subtree, the commissionerate sees the force. Set exactly like
 *  the others — `seed:officer-flag` or mongosh — and held by nobody until somebody is granted it.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const OFFICER_FLAGS = ['access_analytics', 'master_superadmin', 'location_intel'] as const;
export type OfficerFlag = (typeof OFFICER_FLAGS)[number];

export const isOfficerFlag = (value: unknown): value is OfficerFlag =>
  (OFFICER_FLAGS as readonly unknown[]).includes(value);

/** What a stored column may say, reduced to what it can MEAN: declared flags only, each once. */
export const knownFlags = (raw: readonly string[] | null | undefined): OfficerFlag[] => [
  ...new Set((raw ?? []).filter(isOfficerFlag)),
];

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [P7C §5] A SYSTEM USER — AN OFFICER HOLDING `master_superadmin` — IS IN NO OFFICER LIST.
 *
 *  A client decision (U3): every officer LIST and COUNT on both surfaces leaves a holder out — the
 *  dashboard's `GET /officers` and `GET /org-units/counts`, and Duty Mitra's directory, search
 *  included. A by-id read is UNCHANGED, so a holder still signs in, still opens their own record
 *  and is still found by a caller who already has the id.
 *
 *  ⚠️ AND THE FLAG GAINS A SECOND PLACE WHERE, ON SOMEBODY ELSE'S RECORD, IT NARROWS WHAT YOU
 *  **SEE** — beside [P7A §7]'s "what you may DO". A flag still only ADDS to its holder (S ⊆ T is
 *  untouched); what it now does to everybody else is take one row out of their lists.
 *
 *  ⚠️ THE STORED VALUE, NAMED ONCE. This file is the only place `officerFlags.test.ts` F7 allows the
 *  literal; every database predicate reaches it through this identifier, and every READ's predicate
 *  is built in ONE file — `api/src/utils/systemUser.util.ts`. F7b pins the files that may build a
 *  `flags:` predicate at all to that one, the model's partial index and the operator tool's writer.
 *
 *  ⚠️ THE COST, STATED: a name that resolves by id but is absent from every list REVEALS a holder,
 *  which reverses [P7A §7]'s "holder status is not on `OfficerDto`" IN EFFECT. Accepted by U3;
 *  `docs/ops/analytics.md` §5.2 and `docs/api/officer-contract.md` §8a say the same.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const SYSTEM_USER_FLAG = 'master_superadmin' as const satisfies OfficerFlag;

/**
 * ⚠️ CAPABILITIES NO ROLE HOLDS — A SECOND TABLE, DISJOINT FROM `CAPABILITIES`, AND TESTED SO.
 *
 * An empty row inside `CAPABILITIES` was the obvious design and is refused: it would make
 * `web/src/lib/accessRole.test.ts`' "gives SUPER_ADMIN everything" false — a guard edited to pass
 * — and it would put a key into the table `can()` answers from that `can()` could never grant.
 * Kept apart, `can()` stays role-only and a flag capability is reachable through a flag alone.
 *
 * ⚠️ [P7A §7] `superAdmin.administer` — "may change ANOTHER super admin", granted by
 * `master_superadmin` and paired with the SUPER_ADMIN-only role row `officer.writeSuperAdmin`.
 *
 * ⚠️ [LOCATION-INTEL] `locationIntel.access` — "may open Locational Intel", granted by `location_intel`
 * alone and paired with `officer.read` in `LOCATION_INTEL_ACCESS` below.
 */
export const FLAG_CAPABILITIES = [
  'analytics.access',
  'audit.access',
  'superAdmin.administer',
  'arrangementOverride.discardDrafts',
  'arrangementOverride.revertRunning',
  'locationIntel.access',
] as const;
export type FlagCapability = (typeof FLAG_CAPABILITIES)[number];

/**
 * Each flag names what it ADDS. `satisfies` makes a flag without a row a compile error.
 *
 * ⚠️ [amended AUDIT-FLAG] ONE STORED FLAG, TWO FLAG CAPABILITIES — AND THE SECOND IS CO-EXTENSIVE
 * WITH THE FIRST OVER THE WHOLE SET, WHICH IS THE COST STATED WHERE THE RULE WOULD REFUSE IT.
 * `OFFICER_FLAGS` has one member and it grants both, so no officer can hold `analytics.access`
 * without `audit.access` — exactly the shape `CAPABILITIES`' own rule ("a capability always granted
 * alongside another grants nothing") refuses, and more sharply than either exception above, which
 * are co-extensive only by their role rows. Minted anyway on `analytics.read`'s and
 * `settings.write`'s ground, both parts of which transfer: what each GATES is a different subject —
 * the record of who changed what, against network telemetry and where officers stood — and there is
 * now something for a later split to split FROM. **THIS IS THE SEAM.** "The audit log granted
 * separately from the telemetry" is a second `OFFICER_FLAGS` member and one row here, with ZERO
 * edits at any gate site; reuse `analytics.access` for both and that same change edits a value three
 * gates read. ⚠️ So do NOT "simplify" these two into one member.
 *
 * ⚠️ [amended P7A §7] THE SEAM WAS USED — FOR A DIFFERENT SECOND FLAG. `master_superadmin` is the
 * second member and its own row, granting `superAdmin.administer` and nothing the first grants; the
 * audit/telemetry split above is still one row away and still unasked for. The two rows share no
 * flag capability, which `identity.constant.test.ts` holds, so neither flag can open the other's door.
 */
export const FLAG_GRANTS = {
  access_analytics: ['analytics.access', 'audit.access'],
  /*
   * ⚠️ [P7B §6] AND THE TWO ARRANGEMENT OVERRIDES — one flag capability PER ACT, never
   * `superAdmin.administer` reused: that one is named for changing another super admin, and
   * [AUDIT-FLAG] refused exactly that alias. Granted by the same flag because the client asked for the
   * same holders; splitting them later is one row here.
   */
  master_superadmin: [
    'superAdmin.administer',
    'arrangementOverride.discardDrafts',
    'arrangementOverride.revertRunning',
  ],
  /*
   * ⚠️ [LOCATION-INTEL] ITS OWN ROW AND ITS OWN FLAG CAPABILITY, SHARING NOTHING WITH THE OTHER TWO — so
   * the map opens for nobody who was granted only analytics or only the super admin overrides, and
   * neither of those pages opens for a holder of this one. `identity.constant.test.ts` holds the disjointness.
   */
  location_intel: ['locationIntel.access'],
} as const satisfies Record<OfficerFlag, readonly FlagCapability[]>;

/** Not exported: `meetsRequirement` is the only door, and F7 of `officerFlags.test.ts` holds it so. */
const grantedByFlags = (flags: readonly string[] | null | undefined, capability: FlagCapability): boolean =>
  knownFlags(flags).some((flag) => (FLAG_GRANTS[flag] as readonly FlagCapability[]).includes(capability));

/**
 * Who is asking. `/auth/me`'s identity and the API's `AuthContext` both satisfy it as they are.
 * `flags` is optional and nullable so a holder built from a role alone fails CLOSED.
 */
export type CapabilityHolder = {
  readonly accessRole: AccessRole;
  readonly flags?: readonly string[] | null | undefined;
};

/**
 * What a route, a page or a nav row asks for: a role capability alone, or a role capability AND a
 * flag capability. There is no third shape — in particular no "a flag alone" — so a flag can never
 * open a door the role does not already stand at.
 */
export type CapabilityRequirement =
  Capability | { readonly capability: Capability; readonly flagCapability: FlagCapability };

/**
 * ⚠️ THE ONE EXPRESSION OF THE ANALYTICS REQUIREMENT, used by both API routes, the web route and
 * the nav row. Four copies of a pair would be four places for it to drift; one value needs no
 * pinning test to keep them in step.
 */
export const ANALYTICS_ACCESS = {
  capability: 'analytics.read',
  flagCapability: 'analytics.access',
} as const satisfies CapabilityRequirement;

/**
 * ⚠️ [AUDIT-FLAG] THE ONE EXPRESSION OF THE AUDIT REQUIREMENT, used by `GET /audit`, the web route
 * and the nav row — three sites, `ANALYTICS_ACCESS`' argument verbatim.
 *
 * A client decision, reversing [C5b §5]: the one `access_analytics` flag opens BOTH System pages, so
 * a super admin reads the audit log only when granted. `audit.read` still names SUPER_ADMIN alone and
 * a flag only ADDS, so a nodal admin holding the flag is refused here exactly as they are on
 * analytics — the role baseline binds first, in one resolver.
 *
 * ⚠️ ITS OWN FLAG CAPABILITY, NEVER `analytics.access` REUSED. The two are indistinguishable in
 * effect today (`FLAG_GRANTS` states that cost), and the distinction still earns its place twice
 * over: a requirement naming `analytics.access` to gate `audit.read` reads as a mistake at the gate
 * and would need a paragraph of apology at each of the three, and it would leave nothing to split
 * from — the day the client wants these granted separately, this way it is one row of `FLAG_GRANTS`.
 *
 * ⚠️ THE ACCEPTED COST, RECORDED HERE BECAUSE IT CANNOT BE UNDONE BY A GRANT: THE OVERSIGHT
 * POPULATION IS NOW THE DISCLOSURE POPULATION. `readOfficerTrail` commits `OFFICER_TRAIL_VIEWED`
 * before it answers, on the rule that a disclosure is never reported without a record of it — and
 * after this change the only officers who can READ that row are the only officers who can write it.
 * The same holds for the `OFFICER_UPDATED`-with-`phone` row that `docs/ops/analytics.md` §5.1 tells
 * an operator to watch for, whose subject is a flag holder. Answering that needs a SECOND flag
 * (`FLAG_GRANTS` is where it would go), not a change here.
 */
export const AUDIT_ACCESS = {
  capability: 'audit.read',
  flagCapability: 'audit.access',
} as const satisfies CapabilityRequirement;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [P7A §7] THE ONE EXPRESSION OF "MAY CHANGE ANOTHER SUPER ADMIN" — and it is asked TWICE per
 *  write, of two different officers.
 *
 *  `officer.service.ts`' `assertMayAdminister` asks it of the CALLER ("may I act on a super admin
 *  who is not me?") and of the TARGET ("does this super admin hold it too?"). Both answers go
 *  through `meetsRequirement`, so the target's stored flags are reduced by `knownFlags` exactly as
 *  the caller's are — a typo on either record grants nothing — and no membership read exists
 *  outside this file (F7 of `officerFlags.test.ts`).
 *
 *  ⚠️ THE ROLE HALF IS `officer.writeSuperAdmin`, SUPER_ADMIN ALONE, so a NODAL_ADMIN holding the
 *  flag is refused on the role exactly as on analytics; a nodal is held off a super admin by the
 *  rank backstop besides. The web's offer gate (`superAdminPeerRefusal`) asks the same value.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const SUPER_ADMIN_ADMINISTRATION = {
  capability: 'officer.writeSuperAdmin',
  flagCapability: 'superAdmin.administer',
} as const satisfies CapabilityRequirement;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [P7B §6] THE TWO ARRANGEMENT OVERRIDES — each the SUPER_ADMIN-only `lifecycle.override` AND a
 *  flag capability only `master_superadmin` grants. One value each, read by the API route
 *  (`dashboardStack`), the web route gate and the web offer gates in `lib/accessRole.ts` — so a hidden
 *  button and a refused request cannot disagree ([C1c] §7a, [C3c] §9c).
 *
 *  `BULK_DRAFT_DISCARD` — select many drafts on Manage Arrangements and discard them together.
 *  `REVERT_RUNNING_ARRANGEMENT` — take an order whose duty has started back to draft: a deliberate
 *  piercing of [C1d]'s rule that a running order is a record, refused everywhere else.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const BULK_DRAFT_DISCARD = {
  capability: 'lifecycle.override',
  flagCapability: 'arrangementOverride.discardDrafts',
} as const satisfies CapabilityRequirement;

export const REVERT_RUNNING_ARRANGEMENT = {
  capability: 'lifecycle.override',
  flagCapability: 'arrangementOverride.revertRunning',
} as const satisfies CapabilityRequirement;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [LOCATION-INTEL] THE ONE EXPRESSION OF "MAY OPEN LOCATIONAL INTEL" — read by all three API routes
 *  (`dashboardStack`), the web route gate and the nav row, so a hidden row and a refused request cannot
 *  disagree ([C1c] §7a, [C3c] §9c). The nav row is a convenience; the routes refuse on their own.
 *
 *  ⚠️ THE ROLE HALF IS `officer.read`, NOT A SUPER_ADMIN-ONLY ROW, AND THAT IS THE BRIEF'S OWN RULE: a
 *  division's holder sees their own subtree, the commissionerate sees the force. Every dashboard role
 *  holds `officer.read`, and the data is an officer's — the master the capability names. What confines a
 *  holder is `Scope`, applied in the queries; a constable (`NONE`) holding the flag is still refused,
 *  because a flag only ever adds to what the role already allows.
 *
 *  ⚠️ NO CAPABILITY IS MINTED FOR THE ROLE HALF. A `locationIntel.read` row would be granted to exactly
 *  the roles `officer.read` holds — the table's rule that a capability always granted alongside another
 *  grants nothing. The narrowing is the PERSON, which is what a flag is for.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const LOCATION_INTEL_ACCESS = {
  capability: 'officer.read',
  flagCapability: 'locationIntel.access',
} as const satisfies CapabilityRequirement;

/**
 * ⚠️ [P7A §7] THE TWO REFUSALS, read by both sides of the wire — the API throws them and the web's
 * disabled Edit / Transfer shows the first — so the tooltip and the 403 cannot drift ([C3c.2] #671).
 *
 * They say "master super admin" in words and never the stored value: F7's vocabulary census matches
 * the value as an unanchored substring outside this file, and a sentence is read by a person.
 *
 * ⚠️ THE SECOND IS ONLY EVER SEEN BY A HOLDER. The service checks the caller first, so a flagless
 * caller is told the general rule and never learns which super admins hold the flag; only a holder
 * — who could read it off the database anyway — is told the target is one too.
 */
export const SUPER_ADMIN_PEER_REFUSAL =
  'Only a master super admin can change another super admin. Ask one to make this change.';
export const MASTER_SUPER_ADMIN_REFUSAL =
  'This officer is a master super admin, and only they can change their own record.';

/**
 * ⚠️ [P7A §7] WHAT EACH FLAG OPENS, IN A SENTENCE — the `seed:officer-flag` help and report read it, so
 * the console never names a flag literal (F7) and a flag added without a line here is a compile error.
 */
export const OFFICER_FLAG_PURPOSE = {
  access_analytics: 'Opens Usage analytics (with the officer trail) and the Audit log, for a SUPER_ADMIN.',
  master_superadmin:
    'Lets a SUPER_ADMIN change ANOTHER super admin — attributes and phone, transfer, deactivate, role — never one who also holds it. [P7B] Also: discard many drafts at once, and take a running order back to draft.',
  location_intel:
    "Opens Locational Intel: a map of where the officers in the holder's own scope last reported a location, and one officer's name, rank and posting on a click (never a phone). For any dashboard role that can read officers.",
} as const satisfies Record<OfficerFlag, string>;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ THE ONE PLACE EITHER SIDE ANSWERS "MAY THIS OFFICER?".
 *
 *  The role baseline, AND — only when the requirement names one — a flag's grant. It is an AND of
 *  two terms each of which can only ever be true or false, so a flag set that grows can only turn a
 *  `false` into a `true` (exhaustively tested over every role, requirement and pair S ⊆ T). A bare
 *  `Capability` answers exactly what `can()` answers, whatever the flags say.
 *
 *  Fails closed on every input it does not recognise: no flags, `null`, an unknown value, a flag
 *  capability no flag grants, and a role that lacks the baseline.
 *
 *  ⚠️ CALLED BY EXACTLY SIX FILES — this one, `requireCapability` on the API, and the web's
 *  re-export, route gate and nav filter — [amended P7A §7] and `officer.service.ts`, the one SERVICE
 *  caller, because `master_superadmin` is the one rule about TWO officers and only the write knows the
 *  target. `officerFlags.test.ts` F7 holds the list. A seventh is a second door, and a caller that
 *  reads `flags` without it is a second resolver.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const meetsRequirement = (holder: CapabilityHolder, requirement: CapabilityRequirement): boolean =>
  typeof requirement === 'string'
    ? can(holder.accessRole, requirement)
    : can(holder.accessRole, requirement.capability) && grantedByFlags(holder.flags, requirement.flagCapability);

// ── Authentication ───────────────────────────────────────────────────────────────

/** How an officer identified themselves. Resolution is agnostic; this is the outcome. */
export const IDENTIFIER_TYPES = ['phone', 'email'] as const;
export type IdentifierType = (typeof IDENTIFIER_TYPES)[number];

/**
 * The two surfaces. OD-23: the officer surface accepts phone only — which is DATA
 * (an `allow` list handed to one resolver), never a second code path (FR-5.8.4).
 */
export const AUTH_SURFACES = ['dashboard', 'officer'] as const;
export type AuthSurface = (typeof AUTH_SURFACES)[number];

/**
 * FR-5.8.20 — distinct token audiences per surface, asserted by the verifier itself.
 * An officer-surface token presented to the dashboard fails signature verification's
 * audience check, not a hand-written `if` somebody can forget.
 */
export const TOKEN_AUDIENCES = {
  dashboard: 'pams:dashboard',
  officer: 'pams:officer',
} as const satisfies Record<AuthSurface, string>;
export type TokenAudience = (typeof TOKEN_AUDIENCES)[keyof typeof TOKEN_AUDIENCES];

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [P6c §4] `origin` IS COSMETIC. `surface` ABOVE IS NOT. THEY SIT TOGETHER SO THE
 *  DIFFERENCE IS IMPOSSIBLE TO MISS, AND THEY ARE NEVER INTERCHANGEABLE.
 *
 *  `surface` decides the TOKEN AUDIENCE, is frozen onto the OTP row at send time, and is
 *  deliberately not re-suppliable at verify time (see `auth.schema.ts`). `origin` decides
 *  ONE WORD IN AN SMS. It is never stored, never read after the message is composed, and a
 *  mismatched pair — `surface: 'officer'` with `origin: 'admin'` — is VALID and mints an
 *  officer token with the word "Admin" in the message.
 *
 *  ⚠️ THE TWO TUPLES SHARE NO MEMBER, DELIBERATELY. Passing one where the other belongs is
 *  a compile error rather than a silent mis-selection. Do NOT "tidy" these towards each
 *  other, and do not add a cross-field refinement between them: a rule by which `origin`
 *  constrained `surface` would make the cosmetic field an authorisation input, which is the
 *  exact property this arrangement exists to prevent.
 *
 *  ⚠️ AND THE NAMES CARRY THE `OTP_` PREFIX FOR A GREP REASON. `api/src/config/env.ts` has
 *  `originSchema`, `toOrigin`, `corsOrigins` and `invalidCorsOrigins`, and the CORS
 *  middleware reasons about the browser's `Origin` HEADER. A bare `Origin`/`ORIGINS`
 *  identifier here would be unsearchable against all of that.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const OTP_ORIGINS = ['admin', 'pwa', 'app'] as const;
export type OtpOrigin = (typeof OTP_ORIGINS)[number];

/**
 * Which application name a request's `origin` resolves to.
 *
 * ⚠️ [amended P7A §8] IT FILLS NO SMS SLOT ANY MORE. It filled the DLT template's
 * `{application}` slot until [OTP-KP]; since then it reaches the development console line
 * only. The P7A resend backup over Airtel DLT does carry an `{application}` slot, and it is
 * FIXED to `'KP Duty Mitra'` in `api/src/services/sms/airtelDlt.template.ts` rather than read
 * from this map — so `origin` still never reaches a handset, which is the property that matters.
 *
 * `as const satisfies Record<OtpOrigin, string>`, so a fourth origin is a COMPILE ERROR
 * rather than a silently missing branch — `APPLICATION_NAMES`'s exhaustiveness is the whole
 * reason `applicationNameFor` needs no default and no `??`.
 */
export const APPLICATION_NAMES = {
  admin: 'KP Duty Mitra Admin',
  pwa: 'KP Duty Mitra',
  app: 'Duty Mitra',
} as const satisfies Record<OtpOrigin, string>;

/** The ONE resolver. `origin` is turned into a LABEL here and travels no further as an enum. */
export const applicationNameFor = (origin: OtpOrigin): string => APPLICATION_NAMES[origin];

/**
 * ⚠️ [amended P6c] `console` IS A DELIVERY OUTCOME, AND IT NEVER REACHES `/auth/config`.
 *
 * P6c gives the development console its own channel rather than making the no-op print,
 * because "the code was suppressed" and "the code was put on a terminal" are the two facts a
 * forensic reader most needs kept apart, and `sent_channels[]` is where that is recorded.
 *
 * ⚠️ IT IS NOT A CHANNEL AN OPERATOR CAN ENABLE, so it is not in `REAL_CHANNELS` and
 * `enabledChannelNames()` — which feeds the PUBLIC, unauthenticated `/auth/config` — can
 * never report it. That is asserted in `otpDelivery.test.ts` rather than assumed, because
 * this tuple is also `authConfigSchema.channels`' enum and widening a public response by a
 * member that cannot legitimately appear there is a cost taken with eyes open.
 */
export const OTP_CHANNELS = ['sms', 'email', 'noop', 'console'] as const;
export type OtpChannelName = (typeof OTP_CHANNELS)[number];

/**
 * ⚠️ [P7A §8] WHAT `otp_requests.sent_channels[].channel` MAY RECORD — A SUPERSET OF THE ABOVE.
 *
 * `sms_backup` is the resend backup over Airtel DLT: a DELIVERY STEP that runs beside the SMS
 * channel on a resend, never a channel an operator can enable. It is therefore deliberately
 * ABSENT from `OTP_CHANNELS`, which is `authConfigSchema.channels`' enum and feeds the PUBLIC,
 * unauthenticated `/auth/config` — widening that tuple would put a member on a public response
 * that can never legitimately appear there, which is the cost `console` above already pays.
 *
 * A second tuple rather than a detail prefix on `sms`: two `sms` rows for one resend would be
 * ambiguous both to read and to query, where a named outcome says which transport carried it.
 */
export const OTP_OUTCOME_CHANNELS = [...OTP_CHANNELS, 'sms_backup'] as const;
export type OtpOutcomeChannelName = (typeof OTP_OUTCOME_CHANNELS)[number];

/**
 * Six digits, everywhere.
 *
 * ⚠️ [amended P5c] THE DEPENDENCY HAS INVERTED, AND `OTP_BYPASS_CODE` IS GONE FROM HERE.
 *
 * This used to read "Not configurable: `OTP_BYPASS_CODE` below depends on it", and that
 * constant was the literal `'000000'` — declared in `packages/shared`, which meant BOTH
 * browser bundles shipped it. The bypass code is now `BYPASS_OTP`, per deployment, with no
 * default; `api/src/config/env.ts` builds its `^\d{6}$` regex FROM this number.
 *
 * ⚠️ THE LENGTH IS PUBLIC AND THE CODE IS NOT, WHICH IS WHY THE DERIVATION GOES THIS WAY.
 * The OTP input renders this many boxes and `/auth/config` reports it. Deriving the public
 * number from the secret would disclose the secret's shape; deriving the secret's shape
 * from the public number discloses nothing.
 */
export const OTP_CODE_LENGTH = 6;
